import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiZap, FiClock, FiArrowRight } from 'react-icons/fi';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';
import ProductCard from '../../components/product/ProductCard';

const FlashDealsPage = () => {
  const { t, lang } = useLanguage();
  const [activeDeals, setActiveDeals] = useState([]);
  const [upcomingDeals, setUpcomingDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const [activeRes, upcomingRes] = await Promise.all([
          api.get('/flash-deals/active'),
          api.get('/flash-deals/upcoming'),
        ]);
        setActiveDeals(activeRes.data.data || []);
        setUpcomingDeals(upcomingRes.data.data || []);
      } catch { /* noop */ }
      setLoading(false);
    };
    fetchDeals();
  }, [lang]);

  if (loading) {
    return <div className="section-padding text-center"><div className="spinner"></div></div>;
  }

  return (
    <div className="section-padding" style={{ background: '#FFF9F9', minHeight: '80vh' }}>
      <div className="container">
        <div className="d-flex align-items-center gap-2 mb-4">
          <FiZap size={28} color="#EF4444" />
          <h2 style={{ fontWeight: 900, margin: 0 }}>Flash Deals</h2>
        </div>

        {activeDeals.length === 0 && upcomingDeals.length === 0 ? (
          <div className="text-center py-5">
            <FiZap size={48} color="#D1D5DB" />
            <h5 className="text-muted mt-3">{t('noFlashDeals')}</h5>
            <p className="text-muted">Check back soon — new deals drop every day!</p>
            <Link to="/products" className="btn btn-ebw-primary">{t("allProductsTitle")}</Link>
          </div>
        ) : (
          <>
            {activeDeals.map(deal => (
              <div key={deal._id} className="mb-5">
                {/* Deal header */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"
                  style={{ background: 'linear-gradient(135deg, #EF4444, #DC2626)', borderRadius: '12px', padding: '16px 24px' }}>
                  <div>
                    <h4 style={{ fontWeight: 800, color: '#FFF', margin: 0 }}>
                      {(lang !== 'en' && deal.translations?.[lang]?.name) || deal.name} — <span style={{ color: '#FEF3C7' }}>{deal.discountPercent}% OFF</span>
                    </h4>
                    {(deal.description || deal.translations?.[lang]?.description) && <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '14px' }}>{(lang !== 'en' && deal.translations?.[lang]?.description) || deal.description}</p>}
                  </div>
                  <CountdownSmall endDate={deal.endDate} />
                </div>

                {/* Products */}
                <div className="row g-3">
                  {(deal.products || []).map(product => (
                    <div key={product._id} className="col-sm-6 col-md-4 col-lg-3">
                      <ProductCard product={{
                        ...product,
                        price: product.flashPrice,
                        compareAtPrice: product.originalPrice,
                      }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {upcomingDeals.length > 0 && (
              <>
                <h4 className="mt-5 mb-3" style={{ fontWeight: 800, color: '#6B7280' }}>
                  <FiClock size={20} className="me-1" /> {t("comingSoon")}
                </h4>
                {upcomingDeals.map(deal => (
                  <div key={deal._id} className="dashboard-card p-3 mb-3 d-flex justify-content-between align-items-center">
                    <div>
                      <h6 style={{ fontWeight: 700, margin: 0 }}>{(lang !== 'en' && deal.translations?.[lang]?.name) || deal.name}</h6>
                      <span className="text-muted" style={{ fontSize: '13px' }}>
                        {deal.discountPercent}% {t('offLabel')} · {(deal.productIds || []).length} {t('productsLabel')} · {t('startsLabel')} {new Date(deal.startDate).toLocaleDateString()}
                      </span>
                    </div>
                    <span style={{
                      background: '#FEF3C7', color: '#D97706', borderRadius: '8px',
                      padding: '4px 12px', fontWeight: 700, fontSize: '12px',
                    }}>
                      <FiClock size={12} className="me-1" /> {t("upcoming")}
                    </span>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Compact countdown for deal headers
const CountdownSmall = ({ endDate }) => {
  const [tl, setTl] = useState(calc(endDate));
  useEffect(() => {
    const t = setInterval(() => setTl(calc(endDate)), 1000);
    return () => clearInterval(t);
  }, [endDate]);

  if (tl.expired) return <span style={{ color: '#FEE2E2', fontWeight: 700 }}>Ended</span>;

  const parts = [];
  if (tl.days) parts.push(`${tl.days}d`);
  parts.push(`${String(tl.hours).padStart(2, '0')}h`);
  parts.push(`${String(tl.minutes).padStart(2, '0')}m`);
  parts.push(`${String(tl.seconds).padStart(2, '0')}s`);

  return (
    <span style={{ color: '#FFF', fontWeight: 800, fontSize: '18px', fontFamily: 'monospace' }}>
      ⏱ {parts.join(' : ')}
    </span>
  );
};

function calc(endDate) {
  const d = new Date(endDate) - new Date();
  if (d <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    expired: false,
    days: Math.floor(d / 86400000),
    hours: Math.floor((d / 3600000) % 24),
    minutes: Math.floor((d / 60000) % 60),
    seconds: Math.floor((d / 1000) % 60),
  };
}

export default FlashDealsPage;
